// src/lib/clinic-identity.ts
//
// Single source of truth for the PUBLIC clinic identity: name, address,
// public phone numbers, WhatsApp, public email, opening hours and locality
// wording. Import from here instead of hard-coding these values.
//
// Rules for this file:
// - Public information only. Never add secrets, service accounts, internal
//   or staff phone numbers, or privacy/grievance/legal contacts (those are
//   owned by the privacy/legal pages and are intentional exceptions).
// - No environment-variable reads, no React, safe for server AND client.

const addressLines = [
  '319, 7th Main Road',
  'CMR Main Road',
  'HRBR Layout 2nd Block',
  'Kalyan Nagar',
  'Bengaluru',
  'Karnataka 560043',
  'India',
] as const

const formattedAddress = addressLines.join(', ')

export const clinicIdentity = {
  name: 'ReACH Psychiatry',

  address: {
    line1: '319, 7th Main Road',
    line2: 'CMR Main Road',
    area: 'HRBR Layout 2nd Block',
    locality: 'Kalyan Nagar',
    city: 'Bengaluru',
    region: 'Karnataka',
    postalCode: '560043',
    country: 'India',
    countryCode: 'IN',
    /** One-line display form of the full approved address. */
    formatted: formattedAddress,
  },

  contact: {
    /** Primary public call number. Use for every "Call us" CTA. */
    phone: {
      display: '+91 99721 69053',
      e164: '+919972169053',
      href: 'tel:+919972169053',
    },

    /** Clinic landline. Keep as landline — do not swap with the mobile. */
    landline: {
      display: '080 4098 8156',
      e164: '+918040988156',
      href: 'tel:+918040988156',
    },

    /** WhatsApp business number. Use ONLY for WhatsApp CTAs. */
    whatsapp: {
      display: '+91 63601 19204',
      e164: '+916360119204',
      waNumber: '6360119204',
      href: 'https://wa.me/6360119204',
    },

    /** General public email. Privacy/grievance contacts are separate. */
    publicEmail: {
      display: 'reachpsychiatry4u@gmail.com',
      href: 'mailto:reachpsychiatry4u@gmail.com',
    },
  },

  maps: {
    /** Curated Google Maps pin for the clinic. Prefer for directions CTAs. */
    placeUrl: 'https://maps.app.goo.gl/BkUdhTkLUekkEj7p7',
    /** Address-based search fallback (no invented coordinates). */
    searchUrl:
      'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(`${'ReACH Psychiatry'}, ${formattedAddress}`),
  },

  hours: {
    display: 'Monday–Saturday, 10:00 AM–5:00 PM',
    sunday: 'Closed',
    /** schema.org OpeningHoursSpecification-compatible shape. */
    schema: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ],
        opens: '10:00',
        closes: '17:00',
      },
    ],
  },

  /** Approved locality wording for copy and metadata. */
  locality: 'Kalyan Nagar, Bengaluru, Karnataka',
} as const

export type ClinicIdentity = typeof clinicIdentity

/** schema.org PostalAddress built from the approved address. */
export function clinicPostalAddress() {
  const a = clinicIdentity.address
  return {
    '@type': 'PostalAddress',
    streetAddress: `${a.line1}, ${a.line2}, ${a.area}`,
    addressLocality: `${a.locality}, ${a.city}`,
    addressRegion: a.region,
    postalCode: a.postalCode,
    addressCountry: a.countryCode,
  } as const
}
